import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ArrowDownLeft, Calendar, Filter, Gift } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { customerAPI } from '../../services/api';
import { format, formatDistanceToNow } from 'date-fns';

const History = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, earned, redeemed

  useEffect(() => {
    fetchTransactions();
  }, [user]);

  const fetchTransactions = async () => {
    if (!user?.id) return;
    try {
      const response = await customerAPI.getTransactions(user.id);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter;
  });

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const formatFullDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <div className="skeleton h-12 rounded-xl" />
        {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B] mb-2">Points History</h1>
        <p className="text-gray-600">View all your earned and redeemed points</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-2 text-[#8FBC8F] mb-1">
            <ArrowUpRight className="w-5 h-5" />
            <span className="text-sm">Total Earned</span>
          </div>
          <p className="font-serif text-2xl text-[#4A154B]">
            {transactions.filter(t => t.type === 'earned').reduce((sum, t) => sum + t.points, 0)}
          </p>
        </div>
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-2 text-[#C74545] mb-1">
            <ArrowDownLeft className="w-5 h-5" />
            <span className="text-sm">Total Redeemed</span>
          </div>
          <p className="font-serif text-2xl text-[#4A154B]">
            {Math.abs(transactions.filter(t => t.type === 'redeemed').reduce((sum, t) => sum + t.points, 0))}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'earned', 'redeemed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${
              filter === f
                ? 'bg-[#4A154B] text-white'
                : 'bg-[#F5E6D3] text-[#4A154B] hover:bg-[#C9A689] hover:text-white'
            }`}
            data-testid={`filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Transactions List */}
      <div className="space-y-3">
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className="glass rounded-xl p-4 flex items-center gap-4"
              data-testid={`transaction-${transaction.id}`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                transaction.type === 'earned' ? 'bg-[#8FBC8F]/20' : 'bg-[#C9A689]/20'
              }`}>
                {transaction.type === 'earned' ? (
                  <ArrowUpRight className="w-6 h-6 text-[#8FBC8F]" />
                ) : (
                  <Gift className="w-6 h-6 text-[#C9A689]" />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-[#4A154B]">
                      {transaction.type === 'earned' 
                        ? transaction.service_name || 'Points Earned'
                        : transaction.reward_name || 'Reward Redeemed'}
                    </h4>
                    <p className="text-sm text-gray-500" title={formatFullDate(transaction.created_at)}>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {formatDate(transaction.created_at)}
                    </p>
                  </div>
                  <span className={`font-semibold text-lg ${
                    transaction.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'
                  }`}>
                    {transaction.type === 'earned' ? '+' : ''}{transaction.points}
                  </span>
                </div>
                
                {transaction.amount && (
                  <p className="text-xs text-gray-400 mt-1">
                    ${transaction.amount.toFixed(2)} spent
                    {transaction.breakdown && (
                      <span className="ml-2">
                        • {transaction.breakdown.tier_multiplier}x tier 
                        {transaction.breakdown.promo_multiplier > 1 && ` • ${transaction.breakdown.promo_multiplier}x promo`}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
